// src/features/device/hooks/useDeviceDetection.ts

import { useEffect } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  setDeviceType,
  selectDeviceType,
  type DeviceType,
} from "../features/deviceSlice";

interface UseDeviceDetectionReturn {
  deviceType: DeviceType | null;
  isMobile: boolean;
  isDesktop: boolean;
  isDetecting: boolean;
}

export const useDeviceDetection = (): UseDeviceDetectionReturn => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Get current device type from Redux
  const currentDeviceType = useSelector(selectDeviceType);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  /**
   * WHY useEffect:
   *
   * We need to dispatch to Redux whenever isMobile changes
   * useEffect runs when dependencies change
   * This ensures Redux stays in sync with screen size
   */
  useEffect(() => {
    const detectedDeviceType: DeviceType = isMobile ? "mobile" : "desktop";

    // Only dispatch if device type actually changed
    // This prevents unnecessary Redux updates
    if (detectedDeviceType !== currentDeviceType) {
      dispatch(setDeviceType(detectedDeviceType));
    }
  }, [isMobile, currentDeviceType, dispatch]);

  return {
    deviceType: currentDeviceType,
    isMobile: currentDeviceType === "mobile",
    isDesktop: currentDeviceType === "desktop",
    isDetecting: currentDeviceType === null,
  };
};
